'use client';

import { Phone } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import PageHeader from '@/components/layout/PageHeader';
import { useProductContext } from '@/contexts/ProductContext';

// Code-split each product variant — the browser only downloads the one it needs.
const AccessoriesProduct = dynamic(
    () => import('@/components/product/AccessoriesProduct'),
);
const NoVariantsProduct = dynamic(
    () => import('@/components/product/NoVariantsProduct'),
);
const VariantDetails = dynamic(
    () => import('@/components/product/VariantDetails'),
);
const WithVariantsProduct = dynamic(
    () => import('@/components/product/WithVariantsProduct'),
);

export default function ProductVariantsPage({
    initialFormattedVariant,
    prodSrcSet,
    prodBlurDataURL,
    bgSrcSet,
    bgBlurDataURL,
    versaPanelTable,
    octoboxTable,
    heroTitle,
    heroBackgroundImage,
    heroDescription,
    realProduct,
}) {
    const params = useParams();
    const searchParams = useSearchParams();
    const router = useRouter();
    const slug = params.product;
    const variantParam = searchParams.get('variant');

    // Start with the server-resolved product (from realProduct prop) so the
    // content area renders immediately in the initial HTML instead of showing a
    // skeleton that gets replaced after client-side hydration + context lookup.
    // This eliminates the CLS that Lighthouse penalises.
    const [product, setProduct] = useState(realProduct ?? null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const { products } = useProductContext();
    // VERSA_PANEL_TABLE/OCTOBOX_TABLE are server-provided props (see page.js)
    // rather than global context values — they're only relevant on this route,
    // so keeping them out of ProductContext avoids shipping them to every page.
    const VERSA_PANEL_TABLE = versaPanelTable;
    const OCTOBOX_TABLE = octoboxTable;

    // Track the previous variantParam to avoid redundant state updates
    // when handleVariantSelect has already set selectedVariant synchronously
    // before the URL searchParams update triggers this effect.
    const prevVariantParamRef = useRef(null);

    useEffect(() => {
        if (!slug) return;

        const foundProduct = products.find(
            (p) =>
                p.href === `/products/${slug}` ||
                p.name.toLowerCase().replace(/\s+/g, '-') === slug,
        );

        // Only update product state when it actually changed (prevents
        // unnecessary re-renders when only variantParam changed).
        setProduct((prev) => (prev !== foundProduct ? foundProduct : prev));

        // Only process variant from URL when variantParam actually changed.
        // This prevents redundant setSelectedVariant calls when
        // handleVariantSelect already set the state synchronously.
        if (variantParam !== prevVariantParamRef.current) {
            if (variantParam && foundProduct?.variants) {
                const foundVariant = foundProduct.variants.find((v) => {
                    if (!v) return false;
                    const vSlug = v.name
                        .toLowerCase()
                        .replace(/\s+/g, '-')
                        .replace(/[^\w-]/g, '');
                    return vSlug === variantParam;
                });
                setSelectedVariant(foundVariant);
            } else {
                setSelectedVariant(null);
            }
            prevVariantParamRef.current = variantParam;
        }
    }, [slug, variantParam, products]);

    // Note: Document title updates are now handled by Next.js metadata system.
    // Client-side title manipulation was removed because it caused issues with
    // title not updating correctly when navigating back to other pages (e.g., home).
    // The server-rendered metadata in page.js handles SEO-optimized titles properly.

    const handleVariantSelect = (variant) => {
        if (!variant) return;

        const variantSlug = variant.name
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]/g, '');

        // Remove the scroll: false option to allow default scrolling behavior
        router.push(`/products/${slug}?variant=${variantSlug}`);
        setSelectedVariant(variant);

        // Explicitly scroll to top
        window.scrollTo(0, 0);
    };

    const handleBackToProduct = () => {
        router.push(`/products/${slug}`);
        setSelectedVariant(null);

        // Explicitly scroll to top
        window.scrollTo(0, 0);
    };

    if (!product) {
        // The real product (variants, specs, etc) is still resolving from
        // client context, but the hero itself — title, description, and
        // banner image — is already fully known server-side (see page.js,
        // which resolves it from productData.mjs regardless of which variant
        // view eventually renders). Rendering the REAL PageHeader here means
        // the actual LCP image ships in the initial HTML and paints
        // immediately, instead of behind a flat placeholder box that gets
        // replaced later. The card grid below stays a genuine loading
        // skeleton, since that content really does depend on the client-side
        // product/variant lookup finishing.
        return (
            <div className="min-h-screen bg-gray-50">
                <PageHeader
                    title={heroTitle}
                    description={heroDescription}
                    backgroundImage={heroBackgroundImage}
                    bgSrcSet={bgSrcSet}
                    bgBlurDataURL={bgBlurDataURL}
                    breadcrumbs={[
                        { label: 'Home', href: '/' },
                        { label: 'Products', href: '/products' },
                        { label: heroTitle },
                    ]}
                    backLabel="Go Back"
                    primaryAction={{
                        label: 'Contact Sales',
                        href: '/contact',
                        icon: <Phone className="w-4 h-4" />,
                    }}
                />
                {/* Content skeleton — the part that's genuinely still loading */}
                <div
                    className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 animate-pulse"
                    aria-busy="true"
                >
                    <output className="sr-only">Loading product details</output>
                    <div className="h-4 bg-gray-200 rounded w-40 mb-6" />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div
                                key={i}
                                className="bg-white border border-gray-100 rounded shadow-sm overflow-hidden"
                            >
                                <div className="aspect-[4/3] bg-gray-200" />
                                <div className="p-4 space-y-2">
                                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // If a variant is selected, show the variant details
    if (selectedVariant) {
        return (
            <VariantDetails
                variant={selectedVariant}
                product={product}
                productSlug={slug}
                onBackClick={handleBackToProduct}
                VERSA_PANEL_TABLE={VERSA_PANEL_TABLE}
                OCTOBOX_TABLE={OCTOBOX_TABLE}
            />
        );
    }

    // Handle product with no variants
    if (!product.variants || product.variants.length === 0) {
        return (
            <NoVariantsProduct
                product={product}
                slug={slug}
                prodSrcSet={prodSrcSet}
                prodBlurDataURL={prodBlurDataURL}
                bgSrcSet={bgSrcSet}
                bgBlurDataURL={bgBlurDataURL}
                accessories={product.accessories}
            />
        );
    }

    // Handle accessories product
    if (product.name === 'Accessories' && product.variants) {
        return (
            <AccessoriesProduct
                product={product}
                slug={slug}
                onVariantSelect={handleVariantSelect}
                prodSrcSet={prodSrcSet}
                prodBlurDataURL={prodBlurDataURL}
                bgSrcSet={bgSrcSet}
                bgBlurDataURL={bgBlurDataURL}
            />
        );
    }

    // For products with variants
    // Group variants by type
    const variantsByType = {};
    if (product.variants) {
        product.variants.forEach((variant) => {
            if (variant) {
                const type = variant.variantType || 'default';
                if (!variantsByType[type]) {
                    variantsByType[type] = [];
                }
                variantsByType[type].push(variant);
            }
        });
    }

    // Check if any variant is a versa panel
    const hasVersaVariant = product.variants?.some(
        (v) => v && v.variantType === 'versa',
    );

    const hasOctoVariant = product.variants?.some(
        (v) => v && v.variantType === 'octo',
    );

    return (
        <WithVariantsProduct
            product={product}
            slug={slug}
            variantsByType={variantsByType}
            hasVersaVariant={hasVersaVariant}
            hasOctoVariant={hasOctoVariant}
            VERSA_PANEL_TABLE={VERSA_PANEL_TABLE}
            OCTOBOX_TABLE={OCTOBOX_TABLE}
            onVariantSelect={handleVariantSelect}
            prodSrcSet={prodSrcSet}
            prodBlurDataURL={prodBlurDataURL}
            bgSrcSet={bgSrcSet}
            bgBlurDataURL={bgBlurDataURL}
        />
    );
}

export function ClientProduct({
    product,
    variant: _variant,
    variantFormatted,
    prodSrcSet,
    prodBlurDataURL,
    bgSrcSet,
    bgBlurDataURL,
    versaPanelTable,
    octoboxTable,
    heroBackgroundImage,
    heroDescription,
    realProduct,
}) {
    // Note: preserveServerTitle prop was removed because we no longer do client-side
    // title manipulation. Next.js metadata system handles title updates properly.

    return (
        <ProductVariantsPage
            initialFormattedVariant={variantFormatted}
            prodSrcSet={prodSrcSet}
            prodBlurDataURL={prodBlurDataURL}
            bgSrcSet={bgSrcSet}
            bgBlurDataURL={bgBlurDataURL}
            versaPanelTable={versaPanelTable}
            octoboxTable={octoboxTable}
            heroTitle={product?.name}
            heroBackgroundImage={heroBackgroundImage}
            heroDescription={heroDescription}
            realProduct={realProduct}
        />
    );
}
